import { useEffect, useRef } from 'react'
import Swal from 'sweetalert2'
import { useAuthStore } from './auth.store'
import { TIMEOUTS } from '@config/constants'

/**
 * Tiempo máximo permitido para completar el proceso de toma de signos vitales.
 * Es un límite duro: se cumple independientemente de la actividad del usuario.
 *
 * TODO: reemplazar por el valor que devuelva el SSO/backend una vez disponible.
 * Candidatos: context.sessionTimeout del getSessionContext(), o header de la respuesta.
 */
const SESSION_TIMEOUT = TIMEOUTS.SESSION_TIMEOUT

/**
 * Antelación con la que se muestra el aviso antes del vencimiento del proceso.
 * El modal es solo informativo — no hay forma de extender el tiempo del proceso.
 */
const SESSION_WARNING = TIMEOUTS.SESSION_WARNING

/**
 * Hook: temporizador de proceso (sesión de toma de signos vitales).
 *
 * Flujo al autenticarse:
 *  1. A los (SESSION_TIMEOUT - SESSION_WARNING) ms → muestra Swal con countdown
 *     informativo: "El tiempo vencerá en N segundos". El usuario puede cerrarlo
 *     pero la sesión expirará igual al llegar a 0.
 *  2. A los SESSION_TIMEOUT ms → expira la sesión sin importar nada más.
 *     Escribe `reasonCancellation = 'TIME_EXPIRED'` y deshabilita auth.
 *     RequireAuth reacciona y redirige a /expiredlink.
 *
 * A diferencia de useInactivityTracker, este timer NO se puede resetear
 * con actividad del usuario. Es el reloj del proceso, no del usuario.
 *
 * Refs usadas:
 *  - `warningTimerRef`: handle del setTimeout que dispara el aviso previo.
 *  - `expiryTimerRef`: handle del setTimeout que expira la sesión.
 *  - `warningIntervalRef`: handle del setInterval del countdown en el Swal.
 */
export const useSessionTimer = () => {
  const { isAuthenticated, setAuthenticated, setReasonCancellation } = useAuthStore()

  const warningTimerRef   = useRef<ReturnType<typeof setTimeout>  | null>(null)
  const expiryTimerRef    = useRef<ReturnType<typeof setTimeout>  | null>(null)
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Cancela todos los timers e intervals pendientes. */
  const clearAllTimers = () => {
    if (warningTimerRef.current !== null) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (expiryTimerRef.current !== null) {
      clearTimeout(expiryTimerRef.current)
      expiryTimerRef.current = null
    }
    if (warningIntervalRef.current !== null) {
      clearInterval(warningIntervalRef.current)
      warningIntervalRef.current = null
    }
  }

  /**
   * Expira la sesión del proceso.
   * Puede ser llamado por el interval del Swal (countdown llega a 0)
   * o por el hard timer (expiryTimerRef). Ambos son idempotentes: los
   * setters de Zustand ignoran escrituras con el mismo valor.
   */
  const expireSession = () => {
    clearAllTimers()
    Swal.close()
    setReasonCancellation('TIME_EXPIRED')
    setAuthenticated(false)
  }

  /**
   * Muestra el Swal informativo con countdown regresivo.
   * El usuario puede cerrarlo con "Entendido" pero la sesión expirará
   * igual cuando el hard timer (expiryTimerRef) dispare.
   */
  const showSessionWarning = () => {
    let secondsLeft = SESSION_WARNING / 1000

    const buildHtml = (s: number) =>
      `El tiempo para la toma de signos vitales vencerá en <strong>${s}</strong> segundo${s !== 1 ? 's' : ''}.`

    Swal.fire({
      icon: 'warning',
      title: '¡Tiempo por vencer!',
      html: buildHtml(secondsLeft),
      showConfirmButton: true,
      confirmButtonText: 'Entendido',
      showCancelButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        // Countdown visual: actualiza el texto cada segundo
        warningIntervalRef.current = setInterval(() => {
          secondsLeft -= 1
          const container = Swal.getHtmlContainer()
          if (container) container.innerHTML = buildHtml(secondsLeft)

          if (secondsLeft <= 0) {
            // El countdown llegó a 0 — expira la sesión directamente desde aquí.
            // Si el hard timer también dispara en este instante, expireSession()
            // es idempotente y no genera efectos secundarios.
            clearInterval(warningIntervalRef.current!)
            warningIntervalRef.current = null
            expireSession()
          }
        }, 1000)
      },
      willClose: () => {
        // Garantiza que el interval se limpia sin importar cómo se cerró el modal
        // (clic en "Entendido" o cierre programático de expireSession).
        if (warningIntervalRef.current !== null) {
          clearInterval(warningIntervalRef.current)
          warningIntervalRef.current = null
        }
      },
    })
    // No se maneja .then(): si el usuario hace clic en "Entendido",
    // el modal se cierra y el hard timer (expiryTimerRef) sigue corriendo.
  }

  // ---------------------------------------------------------------------------
  // Effect principal
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated) return

    // Aviso informativo: SESSION_WARNING ms antes del vencimiento
    warningTimerRef.current = setTimeout(showSessionWarning, SESSION_TIMEOUT - SESSION_WARNING)

    // Expiración dura: al vencer el tiempo total del proceso
    expiryTimerRef.current = setTimeout(expireSession, SESSION_TIMEOUT)

    // Cleanup: cancela ambos timers si isAuthenticated cambia o el componente desmonta
    return () => clearAllTimers()
  }, [isAuthenticated, setAuthenticated, setReasonCancellation]) // eslint-disable-line react-hooks/exhaustive-deps
  // Las helpers leen refs y setters estables de Zustand → no son deps necesarias.
}
